import * as React from "react"
import { useMutation } from "react-query"
import { FiTrash } from "react-icons/fi"
import { AiOutlineMail } from "react-icons/ai"
import {
  Text,
  Spinner,
  Flex,
  useDisclosure,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Divider,
  IconButton,
  Box,
  Center,
  Badge,
  Avatar,
  Stack,
} from "@chakra-ui/react"

import { NoData } from "./NoData"
import { humanize } from "../lib/helpers/text"
import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { useOrgs } from "./providers/OrgProvider"

interface PendingUserInvitesProps {
  invitations: InvitationsResponse
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
}

export interface InvitationsResponse {
  count: number
  results: [
    {
      uuid: string
      email: string
      role: string
      organization_icon: string
      organization_name: string
    },
  ]
}

interface DeleteInvitationResponse {
  organization: string
}

interface DeleteInvitationRequest {
  invitationId: string
}

export const PendingUserInvites: React.FC<PendingUserInvitesProps> = (props) => {
  const toast = useToast()
  const modalProps = useDisclosure()
  const { currentOrgs } = useOrgs()

  const invitations = props.invitations.results

  const { mutateAsync: deleteInvitation, isLoading: deleteLoading } = useMutation<
    DeleteInvitationResponse,
    unknown,
    DeleteInvitationRequest
  >((data) => fetcher(`invitations/${data.invitationId}/`, { method: "delete" }))

  const handleDeleteInvitation = async (invitationId: string) => {
    try {
      const res = await deleteInvitation({ invitationId })
      if (!res) {
        props.refetch()
        toast({ description: "Successfully deleted invitation" })
      } else {
        toast({ description: "Error deleting invitation", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  return (
    <>
      <Button
        leftIcon={<AiOutlineMail />}
        onClick={modalProps.onOpen}
        isLoading={props.isLoading}
        variant="outline"
      >
        {props.invitations.count} • Pending Invites
      </Button>
      <Modal {...modalProps} isCentered>
        <ModalOverlay zIndex={100}>
          <ModalContent>
            <ModalHeader>Pending invites</ModalHeader>
            <ModalCloseButton />
            <ModalBody p={6} pt={0}>
              {props.isLoading || props.isFetching ? (
                <Center h="50px">
                  <Spinner />
                </Center>
              ) : invitations && invitations.length > 0 ? (
                invitations.map((invitation, index) => (
                  <Box key={invitation.uuid}>
                    <Box py={2}>
                      <Flex align="center">
                        <Flex direction="row" align="center" w="100%">
                          {currentOrgs.length > 1 && (
                            <Avatar
                              mr={4}
                              name={invitation.organization_name}
                              src={invitation.organization_icon}
                              size="sm"
                            />
                          )}
                          <Stack>
                            <Text fontWeight="bold" color="gray.700">
                              {invitation.email}
                            </Text>
                            <Badge fontSize="0.8rem" variant="subtle" colorScheme="cyan" cursor="default">
                              {humanize(invitation.role)}
                            </Badge>
                          </Stack>
                        </Flex>
                        <IconButton
                          icon={<FiTrash />}
                          variant="ghost"
                          aria-label="delete invite"
                          color="gray.700"
                          boxSize="30px"
                          minW="30px"
                          p={1}
                          isDisabled={deleteLoading}
                          onClick={() => handleDeleteInvitation(invitation.uuid)}
                        />
                      </Flex>
                    </Box>
                    {index !== invitations.length - 1 && <Divider />}
                  </Box>
                ))
              ) : (
                <NoData>No pending invitations</NoData>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  )
}
